/**
 * Frontend permission matrix.
 *
 * Single source of truth for "what can this user see / do?" — keep all role
 * checks here so they don't drift across components. When a new role lands,
 * just add a branch to `getPermissions` and every dashboard updates together.
 *
 * Mirrors the backend's require_admin / require_staff / require_finance gating
 * in /app/backend/helpers/auth.py. The backend is ALWAYS authoritative — these
 * flags only control UI visibility (no security-sensitive logic).
 */

const ALL_TABS = [
  'analytics',
  'users',
  'services',
  'quotes',
  'invoices',
  'tickets',
  'credentials',
  'webhooks',
  'marketing',
  'presets',
];

/**
 * @param {{ role?: string, is_client?: boolean } | null | undefined} user
 */
export function getPermissions(user) {
  const role = user?.role || 'client';
  const isAdmin      = role === 'admin';
  const isTechnician = role === 'technician';
  const isAccountant = role === 'accountant';
  const isClient     = role === 'client';
  const isStaff      = isAdmin || isTechnician || isAccountant;

  // Per-tab visibility on the staff-area dashboards.
  const tabs = {
    analytics:   isAdmin || isAccountant,
    users:       isAdmin || isTechnician || isAccountant,  // read-only client picker
    services:    isAdmin,
    quotes:      isAdmin || isTechnician,
    invoices:    isAdmin || isAccountant,
    tickets:     isAdmin || isTechnician,
    credentials: isAdmin,
    webhooks:    isAdmin,
    marketing:   isAdmin,
    presets:     isAdmin || isTechnician,
  };

  const visibleTabs = ALL_TABS.filter((t) => tabs[t]);

  // Where should this user land after login?
  // - Admin & accountant share the multi-tab AdminDashboard at /admin (tabs filter
  //   themselves via the matrix above).
  // - Technicians get their dedicated, simpler workspace at /tech.
  // - Clients land on the standard client portal.
  const homeRoute =
      isAdmin       ? '/admin'
    : isTechnician  ? '/tech'
    : isAccountant  ? '/admin'
    : '/dashboard';

  return {
    role,
    isStaff,
    isAdmin,
    isTechnician,
    isAccountant,
    isClient,
    tabs,
    visibleTabs,

    // Capability flags — used by individual tab components to selectively render
    // destructive / financial buttons.
    canManageQuotes:             isAdmin || isTechnician,    // create / send / view audit
    canDeleteQuotes:             isAdmin,                    // financial action
    canConvertQuotesToInvoices:  isAdmin,                    // financial action
    canManageTickets:            isAdmin || isTechnician,
    canManageInvoices:           isAdmin || isAccountant,    // create + bulk ops + WaveApps sync
    canManageUsers:              isAdmin,                    // mutation only — list is wider
    canManageCredentials:        isAdmin,
    canViewAnalytics:            isAdmin || isAccountant,
    canRunReminderSweep:         isAdmin,                    // ops endpoint
    canViewClientList:           isStaff,                    // GET /admin/users picker

    homeRoute,
  };
}

export default getPermissions;
